import axios from "axios"

import type {
  CatalogCategoriesResponse,
  ProductSearchRequest,
  ProductSearchResponse
} from "@/modules/offers/types/catalog"
import getCurrentLocale from "@/utils/getCurrentLocale"
import { API, PM_API } from "@/utils/http"

const formatPaginatedParams = (queryParams) => {
  if (!queryParams.page) {
    queryParams.page = 1
  }

  if (!queryParams.pageSize) {
    queryParams.pageSize = 10
  }

  const params = {
    ...queryParams,
    limit: queryParams.pageSize,
    offset: (queryParams.page - 1) * queryParams.pageSize
  }

  delete params.page
  delete params.pageSize
  return params
}

export const productCatalogSearch = async ({
  value = "",
  pageSize = 10,
  page = 1,
  query = {},
  executor = () => {}
}: ProductSearchRequest = {}) => {
  const CancelToken = axios.CancelToken
  const paginationQuery = {
    limit: pageSize,
    offset: (page - 1) * pageSize
  }
  const params = {
    q: value,
    ...query,
    ...paginationQuery
  }
  const response = await API.get<ProductSearchResponse>(`/v2/seller/catalog/products/search`, {
    params,
    cancelToken: new CancelToken(executor)
  })

  return response
}

// Categories
export const getCategories = async () => {
  return API.get<CatalogCategoriesResponse>(`/v2/seller/catalog/categories`)
}

// Drafts
export const createProductDraft = ({ payload }) => {
  return PM_API.post(`/products/drafts/`, payload)
}

export const fetchProductDraft = ({ oid }) => {
  return PM_API.get(`/products/drafts/${oid}/`)
}

export const updateProductDraft = ({ payload, oid }) => {
  return PM_API.put(`/products/drafts/${oid}/`, payload)
}

export const completeProductDraft = ({ oid }) => {
  return PM_API.post(`/products/drafts/${oid}/complete/`)
}

// Types
export const fetchProductTypes = ({ queryParams, signal = undefined }) => {
  if (!queryParams.related_category) return { data: { results: [], count: 0 } }

  return PM_API.get(`/product-types/`, {
    params: formatPaginatedParams(queryParams),
    headers: {
      "Accept-Language": getCurrentLocale()
    },
    signal
  })
}

export const fetchAttributesByType = ({ code }) => {
  return PM_API.get(`/product-types/${code}/attributes/?order_by=order`)
}

// Brands
export const fetchProductBrands = ({ queryParams, signal = undefined }) => {
  return PM_API.get(`/brands/`, {
    params: formatPaginatedParams(queryParams),
    headers: {
      "Accept-Language": getCurrentLocale()
    },
    signal
  })
}

export const createBrand = ({ payload }) => {
  return PM_API.post(`/brands/`, payload)
}

// Attributes
export const createAttribute = ({ payload }) => {
  return PM_API.post(`/attributes/`, payload, {
    customConfig: { ignoreInterceptor: true }
  })
}

export const fetchProductAttributes = ({ queryParams, signal = undefined }) => {
  return PM_API.get(`/attributes/`, {
    params: formatPaginatedParams(queryParams),
    headers: {
      "Accept-Language": getCurrentLocale()
    },
    signal
  })
}

export const fetchAttributeValues = ({ queryParams: { code, ...query }, signal = undefined }) => {
  return PM_API.get(`/attributes/${code}/list-values/`, {
    params: formatPaginatedParams(query),
    headers: {
      "Accept-Language": getCurrentLocale()
    },
    signal
  })
}

export const fetchAttributeUnits = ({ queryParams, signal = undefined }) => {
  return PM_API.get(`/attributes/${queryParams.code}/possible-units/`, {
    headers: {
      "Accept-Language": getCurrentLocale()
    },
    signal
  })
}

export const addNewValueToAttribute = ({ code, payload }) => {
  return PM_API.post(`/attributes/${code}/list-values/`, payload, {
    customConfig: { ignoreInterceptor: true }
  })
}

// Images
export const uploadImageOnCdn = (payload) => {
  return PM_API.post(`/images/`, payload)
}

export const translateText = (payload) => {
  return PM_API.post(`/ai/translate`, payload)
}
